import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Form, Input, Switch, Tabs } from "antd";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Edit from "../../../assets/Edit.jsx";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import ConfirmationModal from "../../../components/global/modal/ConfirmationModal.jsx";
import MyPagination from "../../../components/global/pagination/Pagination.jsx";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import useModulePermissions from "../../../hooks/useModulePermissions.jsx";
import {
  CreateSymbolCrmGroupApi,
  DeleteSymbolCrmGroupApi,
  GetSymbolCrmAllSymbolsApi,
  GetSymbolCrmGroupsApi,
  SyncAllSymbolsApi,
  UpdateSymbolCrmGroupApi,
} from "../../../api/request/settings/Symbol.js";

const TAB_GROUPS = "groups";
const TAB_ALL = "all";

export default function Symbol() {
  const navigate = useNavigate();
  const symbolPermissions = useModulePermissions("setting", "symbol");
  const canView = !!symbolPermissions.view;
  const canCreate = !!symbolPermissions.create;
  const canUpdate = !!symbolPermissions.update;
  const canDelete = !!symbolPermissions.delete;

  const [activeTab, setActiveTab] = useState(TAB_GROUPS);
  const [groupForm] = Form.useForm();

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState("add");
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupSubmitLoading, setGroupSubmitLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [allSymbols, setAllSymbols] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allTotal, setAllTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const { data } = await GetSymbolCrmGroupsApi();
      if (data?.success) {
        setGroups(data?.data || []);
      } else {
        toast.error(data?.message || "Failed to load groups.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load groups.",
      );
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const fetchAllSymbols = useCallback(async () => {
    setAllLoading(true);
    try {
      const params = new URLSearchParams({
        PageNumber: String(page),
        PageSize: String(pageSize),
        ApplyPagination: "true",
        SearchText: debouncedSearch || "",
      });
      const { data } = await GetSymbolCrmAllSymbolsApi(params.toString());
      if (data?.success) {
        setAllSymbols(data?.data || []);
        setAllTotal(data?.totalRecords || 0);
      } else {
        toast.error(data?.message || "Failed to load symbols.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load symbols.",
      );
    } finally {
      setAllLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  const fetchSymbolCount = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        PageNumber: "1",
        PageSize: "1",
        ApplyPagination: "true",
      });
      const { data } = await GetSymbolCrmAllSymbolsApi(params.toString());
      if (data?.success) setAllTotal(data?.totalRecords || 0);
    } catch {
      // Keep the last known count if the tally request fails.
    }
  }, []);

  useEffect(() => {
    if (canView) {
      fetchGroups();
      fetchSymbolCount();
    }
  }, [canView, fetchGroups, fetchSymbolCount]);

  useEffect(() => {
    if (canView && activeTab === TAB_ALL) fetchAllSymbols();
  }, [canView, activeTab, fetchAllSymbols]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const openAddGroupModal = useCallback(() => {
    setGroupModalMode("add");
    setEditingGroup(null);
    groupForm.resetFields();
    groupForm.setFieldsValue({ isActive: true });
    setGroupModalOpen(true);
  }, [groupForm]);

  const openEditGroupModal = useCallback(
    (record) => {
      setGroupModalMode("edit");
      setEditingGroup(record);
      groupForm.setFieldsValue({
        name: record.name,
        isActive: Boolean(record.isActive),
      });
      setGroupModalOpen(true);
    },
    [groupForm],
  );

  const handleGroupModalOpenChange = useCallback(
    (open) => {
      if (!open) {
        groupForm.resetFields();
        setEditingGroup(null);
      }
      setGroupModalOpen(open);
    },
    [groupForm],
  );

  const handleGroupSubmit = async (values) => {
    setGroupSubmitLoading(true);
    try {
      const payload = {
        name: values.name?.trim(),
        isActive: Boolean(values.isActive),
      };
      const { data } =
        groupModalMode === "add"
          ? await CreateSymbolCrmGroupApi(payload)
          : await UpdateSymbolCrmGroupApi(editingGroup.id, payload);

      if (data?.success) {
        toast.success(data?.message);
        handleGroupModalOpenChange(false);
        await fetchGroups();
      } else {
        toast.error(data?.message || "Failed to save group.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to save group.",
      );
    } finally {
      setGroupSubmitLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      const { data } = await DeleteSymbolCrmGroupApi(deleteTarget.id);
      if (data?.success) {
        toast.success(data?.message);
        setDeleteOpen(false);
        setDeleteTarget(null);
        await fetchGroups();
        await fetchSymbolCount();
        if (activeTab === TAB_ALL) await fetchAllSymbols();
      } else {
        toast.error(data?.message || "Failed to delete group.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to delete group.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const { data } = await SyncAllSymbolsApi();
      if (data?.success) {
        toast.success(data?.message || "Symbols synced successfully.");
        await fetchSymbolCount();
        if (activeTab === TAB_ALL) await fetchAllSymbols();
      } else {
        toast.error(data?.message || "Symbol sync failed.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Symbol sync failed.",
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const groupColumns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (text) => (
          <span className="text-sm text-gray-900">{text || "—"}</span>
        ),
      },
      {
        title: "Status",
        key: "isActive",
        width: 140,
        render: (_, record) => (
          <span className="inline-flex items-center gap-2 text-sm text-gray-700">
            <span
              className={`h-2 w-2 rounded-full ${
                record.isActive ? "bg-emerald-500" : "bg-gray-400"
              }`}
            />
            {record.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        title: "Symbols",
        dataIndex: "symbolCount",
        key: "symbolCount",
        width: 120,
        render: (count) => (
          <span className="text-sm text-gray-800">{count ?? 0}</span>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 220,
        align: "right",
        render: (_, record) => (
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            {canUpdate && (
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() =>
                  navigate(`/symbol_group/${record.id}`, {
                    state: { groupName: record.name },
                  })
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  className="h-4 w-4"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
                Add symbols
              </button>
            )}
            {canUpdate && (
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Edit group"
                title="Edit group"
                onClick={() => openEditGroupModal(record)}
              >
                <Edit />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-red-50"
                aria-label="Delete group"
                title="Delete group"
                onClick={() => {
                  setDeleteTarget(record);
                  setDeleteOpen(true);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="#D91819"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>
              </button>
            )}
          </div>
        ),
      },
    ],
    [canUpdate, canDelete, openEditGroupModal, navigate],
  );

  const allSymbolColumns = useMemo(
    () => [
      {
        title: "Symbol",
        dataIndex: "symbolName",
        key: "symbolName",
        render: (text) => text || "—",
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (text) => text || "—",
      },
      {
        title: "Path",
        dataIndex: "symbolPath",
        key: "symbolPath",
        render: (text) => text || "—",
      },
      {
        title: "Digits / Point",
        key: "digitsPoint",
        width: 140,
        render: (_, record) =>
          `${record.digits ?? 0} / ${record.symbolPoint ?? 0}`,
      },
      {
        title: "Contract",
        dataIndex: "contract",
        key: "contract",
        width: 120,
        render: (value) => (value === 0 || value ? value : "—"),
      },
      {
        title: "Group",
        dataIndex: "groupName",
        key: "groupName",
        render: (text) =>
          text ? (
            <span className="inline-flex items-center rounded-full bg-[#F4F1FF] px-2.5 py-0.5 text-xs font-medium text-primary">
              {text}
            </span>
          ) : (
            <span className="text-gray-400">Unassigned</span>
          ),
      },
    ],
    [],
  );

  const tabItems = [
    {
      key: TAB_GROUPS,
      label: `Groups (${groups.length})`,
      children: (
        <div className="bg-white symbol-groups-table">
          <TableWithSkeleton
            rowKey="id"
            columns={groupColumns}
            data={groups}
            loading={groupsLoading}
            hiddenColumns={!canUpdate && !canDelete ? [""] : []}
            scroll={false}
          />
        </div>
      ),
    },
    {
      key: TAB_ALL,
      label: `All symbols (${allTotal})`,
      children: (
        <div>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
            <Input.Search
              allowClear
              placeholder="Search symbol, description, path, or group"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="max-w-md"
            />
          </div>
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <TableWithSkeleton
              rowKey="id"
              columns={allSymbolColumns}
              data={allSymbols}
              loading={allLoading}
              page={page}
              pageSize={pageSize}
            />
          </div>
          <div className="mt-4">
            <MyPagination
              total={allTotal}
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
            />
          </div>
        </div>
      ),
    },
  ];

  if (!canView) {
    return (
      <div className="border border-gray-200 rounded-xl bg-white p-6 text-gray-600">
        You do not have permission to view symbols.
      </div>
    );
  }

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        tabBarExtraContent={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              disabled={syncLoading || !canUpdate}
              onClick={handleSync}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                className={`h-4 w-4 ${syncLoading ? "animate-spin" : ""}`}
              >
                <path d="M4.5 12a7.5 7.5 0 0 1 12.4-5.7M19.5 12a7.5 7.5 0 0 1-12.4 5.7" />
                <path d="M16 4.5v4h4M8 19.5v-4H4" />
              </svg>
              {syncLoading ? "Syncing..." : "Sync all symbols"}
            </button>
            {canCreate && (
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-white themeHover"
                onClick={openAddGroupModal}
              >
                + New group
              </button>
            )}
          </div>
        }
      />

      <Drawer
        title={
          groupModalMode === "add" ? "New symbol group" : "Edit symbol group"
        }
        placement="right"
        width={400}
        open={groupModalOpen}
        onClose={() => handleGroupModalOpenChange(false)}
        destroyOnClose
      >
        <Form
          form={groupForm}
          layout="vertical"
          autoComplete="off"
          onFinish={handleGroupSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter group name." }]}
          >
            <Input placeholder="e.g. Majors" className="h-11" maxLength={200} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Off" />
          </Form.Item>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-full border border-gray-200 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => handleGroupModalOpenChange(false)}
            >
              Cancel
            </button>
            <LoadableButton
              type="Submit"
              className="h-9 bg-primary text-sm text-white font-medium rounded-full px-5 themeHover"
              lable={groupModalMode === "add" ? "Create group" : "Update"}
              isLoading={groupSubmitLoading}
              disabled={groupSubmitLoading}
              loadingLable={
                groupModalMode === "add" ? "Saving..." : "Updating..."
              }
            />
          </div>
        </Form>
      </Drawer>

      <ConfirmationModal
        isOpen={deleteOpen}
        setIsOpen={setDeleteOpen}
        message="Delete this group? Its symbol assignments will be cleared."
        onConfirm={handleDeleteConfirm}
        isLoading={deleteLoading}
        loadingLabel="Deleting..."
      />

      <style>{`
        .symbol-groups-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #64748b;
          font-weight: 500;
          font-size: 13px;
          border-bottom: 1px solid #eef2f7 !important;
        }
        .symbol-groups-table .ant-table-tbody > tr > td {
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9 !important;
          padding-top: 14px !important;
          padding-bottom: 14px !important;
        }
        .symbol-groups-table .ant-table table {
          table-layout: auto;
        }
        .symbol-groups-table .ant-table-container,
        .symbol-groups-table .ant-table {
          border: none !important;
        }
      `}</style>
    </div>
  );
}
